import Decimal from "decimal.js";
import { toDecimal } from "./decimal";

/**
 * Evaluates a basic arithmetic expression and returns the result as a Decimal.
 *
 * Supports `+ - * /`, parentheses and correct operator precedence, plus unary
 * minus/plus (e.g. `-3`, `2*-3`). The multiplication sign may be typed as `*`,
 * `x`, `X` or `×`; division as `/` or `÷`.
 *
 * Decimal separators: both `,` and `.` are accepted as the decimal point.
 * Grouping / thousand separators are intentionally NOT supported inside
 * expressions because a `.` would be ambiguous (decimal vs. group).
 *
 * Returns `null` for empty/whitespace-only input. Throws an {@link ExpressionError}
 * on a malformed expression (unknown token, unbalanced parentheses, division by
 * zero, etc.) so the caller can decide how to surface the failure.
 *
 * Implementation is a self-contained tokenizer + shunting-yard evaluator — no
 * `eval`/`Function`, so it is safe to run on untrusted user input. All arithmetic
 * is done with decimal.js to avoid floating-point drift.
 */
export class ExpressionError extends Error {}

type Token =
	| { type: "num"; value: Decimal }
	| { type: "op"; op: string; unary: boolean }
	| { type: "lparen" }
	| { type: "rparen" };

// Higher binds tighter. Unary operators sit above the binary ones.
const PRECEDENCE: Record<string, number> = {
	"u-": 4,
	"u+": 4,
	"*": 3,
	"/": 3,
	"+": 2,
	"-": 2,
};

const RIGHT_ASSOCIATIVE = new Set(["u-", "u+"]);

function normalize(input: string): string {
	return input.replace(/[xX×]/g, "*").replace(/÷/g, "/").replace(/,/g, ".");
}

function tokenize(input: string): Token[] {
	const tokens: Token[] = [];
	let i = 0;

	// An operand is expected at the start, after an operator, or after "(".
	const expectingOperand = (): boolean => {
		const prev = tokens[tokens.length - 1];
		return prev == null || prev.type === "op" || prev.type === "lparen";
	};

	while (i < input.length) {
		const char = input[i];

		if (char === " " || char === "\t") {
			i++;
			continue;
		}

		if (char === "(") {
			tokens.push({ type: "lparen" });
			i++;
			continue;
		}

		if (char === ")") {
			tokens.push({ type: "rparen" });
			i++;
			continue;
		}

		if (char === "+" || char === "-" || char === "*" || char === "/") {
			const unary = (char === "+" || char === "-") && expectingOperand();
			tokens.push({ type: "op", op: unary ? `u${char}` : char, unary });
			i++;
			continue;
		}

		if ((char >= "0" && char <= "9") || char === ".") {
			let number = "";
			while (i < input.length && ((input[i] >= "0" && input[i] <= "9") || input[i] === ".")) {
				number += input[i];
				i++;
			}
			if (number === "." || (number.match(/\./g)?.length ?? 0) > 1) {
				throw new ExpressionError(`Invalid number: "${number}"`);
			}
			tokens.push({ type: "num", value: toDecimal(number) });
			continue;
		}

		throw new ExpressionError(`Unexpected character: "${char}"`);
	}

	return tokens;
}

/** Converts the token stream to Reverse Polish Notation (shunting-yard). */
function toRpn(tokens: Token[]): Token[] {
	const output: Token[] = [];
	const operators: Token[] = [];

	for (const token of tokens) {
		if (token.type === "num") {
			output.push(token);
		} else if (token.type === "op") {
			while (operators.length > 0) {
				const top = operators[operators.length - 1];
				if (top.type !== "op") break;
				const higher = PRECEDENCE[top.op] > PRECEDENCE[token.op];
				const equalLeft = PRECEDENCE[top.op] === PRECEDENCE[token.op] && !RIGHT_ASSOCIATIVE.has(token.op);
				if (higher || equalLeft) {
					output.push(operators.pop()!);
				} else {
					break;
				}
			}
			operators.push(token);
		} else if (token.type === "lparen") {
			operators.push(token);
		} else {
			// rparen: pop until matching lparen.
			let foundLparen = false;
			while (operators.length > 0) {
				const top = operators.pop()!;
				if (top.type === "lparen") {
					foundLparen = true;
					break;
				}
				output.push(top);
			}
			if (!foundLparen) {
				throw new ExpressionError("Unbalanced parentheses");
			}
		}
	}

	while (operators.length > 0) {
		const top = operators.pop()!;
		if (top.type === "lparen" || top.type === "rparen") {
			throw new ExpressionError("Unbalanced parentheses");
		}
		output.push(top);
	}

	return output;
}

function evaluateRpn(rpn: Token[]): Decimal {
	const stack: Decimal[] = [];

	for (const token of rpn) {
		if (token.type === "num") {
			stack.push(token.value);
			continue;
		}

		if (token.type !== "op") {
			throw new ExpressionError("Malformed expression");
		}

		if (token.unary) {
			const operand = stack.pop();
			if (operand == null) throw new ExpressionError("Malformed expression");
			stack.push(token.op === "u-" ? operand.negated() : operand);
			continue;
		}

		const right = stack.pop();
		const left = stack.pop();
		if (left == null || right == null) {
			throw new ExpressionError("Malformed expression");
		}

		switch (token.op) {
			case "+":
				stack.push(left.add(right));
				break;
			case "-":
				stack.push(left.sub(right));
				break;
			case "*":
				stack.push(left.mul(right));
				break;
			case "/":
				if (right.isZero()) throw new ExpressionError("Division by zero");
				stack.push(left.div(right));
				break;
			default:
				throw new ExpressionError(`Unknown operator: "${token.op}"`);
		}
	}

	if (stack.length !== 1) {
		throw new ExpressionError("Malformed expression");
	}

	return stack[0];
}

export function evaluateExpression(input: string): Decimal | null {
	const normalized = normalize(input).trim();
	if (normalized === "") return null;

	const tokens = tokenize(normalized);
	if (tokens.length === 0) return null;

	return evaluateRpn(toRpn(tokens));
}
